
import EditorViewer from '@/_components/editorJS/viewer/viewer.vue'
import Editor from '@/components/Editor/Editor.vue'
import ProjectMixin from '@/_mixins/project'
import { Component, Prop } from 'vue-property-decorator'
import { getRequest, postRequest } from '@/plugins/api-axios'
import { Mixins } from 'vue-mixin-decorator'
import { deserialise, serialise } from 'kitsu-core'
import { languages } from '@/types/languages'
import FormCheckboxGroup from '@/views/Project/Form/components/formCheckboxGroup.vue'
import DialogTemplate from '@/_components/dialog/template/template.vue'
import FormResponse from '@/views/Project/Form/components/formResponse.vue'
import { userClient } from '@/apolloClient'
import { PREFERRED_LANGUAGE_QUERY } from '@/views/Project/ch/components/Form/gql/queries'
import type MixinInterface from '@/_mixins'

@Component({
    mixins: [ProjectMixin],
    components: {
        DialogTemplate,
        FormCheckboxGroup,
        FormResponse,
        EditorViewer,
        Editor,
    },
})
export default class RiskAssessmentFormResponseDialog extends Mixins<MixinInterface>(ProjectMixin) {
    @Prop() company!: any
    @Prop() formList!: any[]

    selectedForm: any = null
    selectedLanguage = ''
    formResponse = null
    latestFormResponse = null
    activeRevision = null

    // loading states
    fetchingForms = false
    fetchingFormLanguages = false
    isPending = false
    saveStatus: 'saving' | 'saved' | null = null

    get languages() {
        if (!this.activeRevision?.data?.languages) return []

        return this.activeRevision.data.languages.map((language: string) => {
            let lang = languages.find((lang) => lang['639-2'] === language)
            return {
                text: lang?.['language-name'],
                value: language,
            }
        })
    }

    get isAvailableResponseCompanies() {
        if (!this.selectedForm) return true
        return this.selectedForm.availableResponseCompanies?.data?.find(
            (company: any) => company.id === this.company.company.data.id
        )
    }

    get canSubmit() {
        return Boolean(
            this.selectedForm &&
                this.selectedLanguage &&
                this.activeRevision &&
                this.isAvailableResponseCompanies
        )
    }

    handleSaveStatusChange(status: 'saving' | 'saved' | null) {
        this.saveStatus = status
        if (status) {
            setTimeout(() => {
                this.saveStatus = null
            }, 3000)
        }
    }

    async createFormResponse() {
        if (!this.selectedForm) return

        const payload = serialise('contractorFormResponse', {
            initialLanguageId: this.selectedLanguage,
            company: {
                data: {
                    type: 'company',
                    id: this.company.company.data.id,
                },
            },
            form: {
                data: {
                    type: 'contractorForm',
                    id: this.selectedForm.id,
                },
            },
            writeAccess: {
                data: [
                    {
                        id: this.currentProjectUser._id,
                        type: 'projectUser',
                    },
                ],
            },
        })

        return postRequest(
            `${this.contractorBaseUrl}/contractorFormResponses`,
            this.getRequestHeaders('POST'),
            payload.data
        )
    }

    async start() {
        if (!this.canSubmit) return

        try {
            this.isPending = true
            let openResponse, latestResponse

            // check if contractor form response exists
            let { data: contractorFormResponses } = await getRequest(
                `${this.contractorBaseUrl}/contractorFormResponses`,
                this.getRequestHeaders(),
                {
                    'filter[company]': this.company.company.data.id,
                    'filter[form]': this.selectedForm.id,
                    'filter[form.project]': this.currentProjectId,
                    'page[limit]': 2,
                    sort: '-updatedAt',
                    skipLinks: '',
                }
            )

            ;[openResponse, latestResponse] = deserialise(contractorFormResponses).data
            if (openResponse && openResponse.status !== 'open') {
                latestResponse = openResponse
                openResponse = null
            }
            if (latestResponse?.status === 'open') {
                latestResponse = null
            }

            const openResponseRevisionChanged =
                openResponse?.status === 'open' &&
                openResponse?.version !== this.activeRevision?.version
            const ids = {
                open: openResponse?.id,
                latest: latestResponse?.id,
                prevOpen: openResponseRevisionChanged ? openResponse?.id : null,
            }

            if (!openResponse || openResponseRevisionChanged) {
                let formResponse = await this.createFormResponse()
                openResponse = formResponse.data.data
                ids.open = openResponse.id
            }

            let searchFormResponses = await getRequest(
                `${this.formBaseUrl}/formResponses`,
                this.getRequestHeaders(),
                {
                    'filter[resource]': Object.values(ids).filter(Boolean).join(','),
                    include: 'revision,resource',
                    skipLinks: '',
                }
            )

            let { data } = deserialise(searchFormResponses.data)

            this.formResponse = data.find(
                (formResponse: any) => formResponse.resource.data.id === openResponse.id
            )

            if (openResponseRevisionChanged && this.formResponse) {
                const prevOpen = data.find(
                    (formResponse: any) => formResponse.resource.data.id === ids.prevOpen
                )
                this.formResponse.inputData = prevOpen?.inputData || null
            }

            if (latestResponse) {
                this.latestFormResponse = data.find(
                    (formResponse: any) => formResponse.resource.data.id === latestResponse.id
                )
            }
        } finally {
            this.isPending = false
        }
    }

    async getActiveFormRevision() {
        if (!this.selectedForm || !this.isAvailableResponseCompanies) return

        try {
            this.fetchingFormLanguages = true
            this.selectedLanguage = ''

            let { data: formData } = await getRequest(
                `${this.formBaseUrl}/forms`,
                this.getRequestHeaders(),
                {
                    'filter[resource]': this.selectedForm.id,
                    include: 'activeRevision',
                }
            )
            let [formRevision] = deserialise(formData).data
            this.activeRevision = formRevision.activeRevision.data
            let preferredLanguage

            if (this.activeRevision?.data?.languages?.length > 1) {
                const preferredLanguageResult = await userClient.query({
                    query: PREFERRED_LANGUAGE_QUERY,
                    variables: {
                        candidates: this.activeRevision.data.languages,
                    },
                    fetchPolicy: 'network-only',
                })
                preferredLanguage = preferredLanguageResult.data?.myPreferredLanguage
            }

            if (preferredLanguage) {
                this.selectedLanguage = preferredLanguage
            } else if (this.activeRevision?.data?.languages?.length === 1) {
                this.selectedLanguage = this.activeRevision.data.languages[0]
            }
        } finally {
            this.fetchingFormLanguages = false
        }
    }

    async viewResponse(response: any) {
        if (!response?.resource?.data?.id) {
            return
        }

        const formResponse = await getRequest(
            `${this.contractorBaseUrl}/contractorFormResponses/${response.resource.data.id}`,
            {},
            { include: 'form,readAccess,writeAccess,submittedBy' }
        )
        this.$emit('viewResponse', deserialise(formResponse.data).data)
    }

    async created() {
        if (!this.currentProjectId) return

        this.fetchingForms = true

        if (this.formList.length === 1) {
            ;[this.selectedForm] = this.formList

            await this.getActiveFormRevision()

            // start the form response process if there is only one language and form
            if (this.languages.length === 1) await this.start()
        }

        this.$watch('selectedForm', this.getActiveFormRevision)

        this.fetchingForms = false
    }
}
